import { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  TextField,
  Typography,
  Chip,
  Stack,
  useTheme,
  alpha,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Storage as StorageIcon,
  RocketLaunch as RocketLaunchIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  GitHub as GitHubIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

import StepIndicator from "./StepIndicator";
import Loader from "../Loading";
import { createAppFromTemplate } from "../../helpers/utils";

const STEPS = [
  { id: "name", label: "Name", icon: RocketLaunchIcon },
  { id: "review", label: "Review", icon: StorageIcon },
  { id: "confirm", label: "Confirm", icon: GitHubIcon },
];

const NAME_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * 3-step lightweight wizard driven by a picked app_templates row:
 *   1. Name   — appName input, live-validated against existing apps
 *   2. Review — read-only summary of what will be created
 *   3. Confirm — POST /api/v1/apps/from-template
 *
 * On success calls onCreated({appId, buildId, bindings}) so the
 * caller can redirect to /apps/{id}/deploying.
 */
export default function TemplateProvisionDialog({
  open,
  template,
  projectId,
  existingAppNames = [],
  onClose,
  onCreated,
  showError,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [appName, setAppName] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const services = template?.daariServices || template?.daari_services || [];

  const nameError = useMemo(() => {
    if (!touched) return null;
    if (!appName) return "App name is required.";
    if (!NAME_RE.test(appName)) {
      return "Lowercase letters, numbers, and hyphens only.";
    }
    if (existingAppNames.some((n) => n?.toLowerCase() === appName.toLowerCase())) {
      return "An app with this name already exists in this project.";
    }
    return null;
  }, [appName, touched, existingAppNames]);

  const canProceedFromName = touched && !nameError && appName.length > 0;

  const reset = useCallback(() => {
    setStep(0);
    setDirection(1);
    setAppName("");
    setTouched(false);
    setSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    reset();
    onClose?.();
  }, [submitting, reset, onClose]);

  const goTo = useCallback(
    (next) => {
      setDirection(next > step ? 1 : -1);
      setStep(next);
    },
    [step]
  );

  const handleNameNext = useCallback(() => {
    setTouched(true);
    if (!appName || !NAME_RE.test(appName)) return;
    if (existingAppNames.some((n) => n?.toLowerCase() === appName.toLowerCase())) return;
    goTo(1);
  }, [appName, existingAppNames, goTo]);

  const handleConfirm = useCallback(() => {
    if (submitting) return;
    setSubmitting(true);

    createAppFromTemplate(
      { projectId, appName, templateSlug: template?.slug },
      (result, err) => {
        setSubmitting(false);
        if (err) {
          showError?.(err);
          return;
        }
        onCreated?.({ ...result, appName, template });
        reset();
      }
    );
  }, [submitting, projectId, appName, template, showError, onCreated, reset]);

  if (!template) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          pt: 2.5,
          pb: 1,
        }}
      >
        <Box sx={{ fontSize: "1.6rem" }}>{template?.emoji || "🚀"}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
            {template?.displayName || template?.display_name}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pb: 1 }}>
        <StepIndicator steps={STEPS} currentStep={step} completedSteps={[]} onStepClick={() => {}} />
      </Box>

      <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
        <AnimatePresence mode="wait" custom={direction}>
          {step === 0 && (
            <StepPane key="name" direction={direction}>
              <Typography variant="body2" sx={{ color: isDarkMode ? "#94a3b8" : "#64748b", mb: 2 }}>
                Name your app. This becomes part of its URL and resource names.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="App name"
                value={appName}
                onChange={(e) => {
                  setTouched(true);
                  setAppName(e.target.value.trim());
                }}
                onKeyDown={(e) => e.key === "Enter" && canProceedFromName && handleNameNext()}
                error={!!nameError}
                helperText={nameError || " "}
                placeholder="my-postgres-app"
              />
            </StepPane>
          )}

          {step === 1 && (
            <StepPane key="review" direction={direction}>
              <Stack spacing={2}>
                <ReviewRow label="Template" value={template?.displayName || template?.display_name} />
                <ReviewRow label="App name" value={appName} />
                <ReviewRow
                  label="Git repository"
                  value={template?.gitUrl || template?.git_url}
                  mono
                />
                {services.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: isDarkMode ? "#64748b" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}
                    >
                      Services that will be created
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                      {services.map((svc, i) => {
                        const type = svc?.type || svc;
                        return (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 2,
                              background: isDarkMode ? alpha("#fb923c", 0.08) : alpha("#fb923c", 0.08),
                              border: `1px solid ${alpha("#fb923c", 0.25)}`,
                            }}
                          >
                            <Chip label={type} size="small" sx={{ height: 20, fontSize: "0.68rem" }} />
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                              {appName}-{type}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: isDarkMode ? "#64748b" : "#94a3b8" }}>
                  First deploy will take ~3 min.
                </Typography>
              </Stack>
            </StepPane>
          )}

          {step === 2 && (
            <StepPane key="confirm" direction={direction}>
              <Typography variant="body2" sx={{ color: isDarkMode ? "#94a3b8" : "#64748b", mb: 1 }}>
                Ready to create <strong>{appName}</strong> from{" "}
                <strong>{template?.displayName || template?.display_name}</strong>.
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                We'll provision the Daari services above, create the app, and bind them
                automatically. You'll land on a live deploy status page.
              </Typography>
            </StepPane>
          )}
        </AnimatePresence>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3 }}>
          <Button
            variant="outlined"
            onClick={step === 0 ? handleClose : () => goTo(step - 1)}
            disabled={submitting}
            startIcon={step > 0 ? <KeyboardArrowLeft /> : null}
            sx={{ borderRadius: "10px", textTransform: "none" }}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>

          {step === 0 && (
            <Button
              variant="contained"
              onClick={handleNameNext}
              disabled={!canProceedFromName}
              endIcon={<KeyboardArrowRight />}
              sx={{ borderRadius: "10px", textTransform: "none", background: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)" }}
            >
              Next
            </Button>
          )}
          {step === 1 && (
            <Button
              variant="contained"
              onClick={() => goTo(2)}
              endIcon={<KeyboardArrowRight />}
              sx={{ borderRadius: "10px", textTransform: "none", background: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)" }}
            >
              Next
            </Button>
          )}
          {step === 2 && (
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={submitting}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 3,
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              }}
            >
              {submitting ? <Loader specificDetails={{ size: 20, style: { color: "#fff" } }} /> : "Create app"}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function StepPane({ children, direction }) {
  return (
    <Box
      component={motion.div}
      custom={direction}
      initial={{ opacity: 0, x: direction > 0 ? 24 : -24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction > 0 ? -24 : 24 }}
      transition={{ duration: 0.2 }}
    >
      {children}
    </Box>
  );
}

function ReviewRow({ label, value, mono }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: isDarkMode ? "#64748b" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: mono ? "monospace" : "inherit",
          fontSize: mono ? "0.8rem" : "0.9rem",
          color: isDarkMode ? "#f1f5f9" : "#1e293b",
          wordBreak: "break-all",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}
